import type { KeyedMutator } from 'swr';

import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import axiosInstance from '@/lib/axios';
import { toast } from 'sonner';
import type { Community } from '@/types';

interface FeatureSettingsProps {
  communityId: string;
  /** Revalidates `/api/communities/:id` so layout and caches stay in sync after each toggle. */
  mutateCommunity: KeyedMutator<{ community: Community }>;
  isBusinessEnabled: boolean;
  setIsBusinessEnabled: (val: boolean) => void;
  isBusinessDirectoryEnabled: boolean;
  setIsBusinessDirectoryEnabled: (val: boolean) => void;
  isGivesAsksEnabled: boolean;
  setIsGivesAsksEnabled: (val: boolean) => void;
  isLeadEnabled: boolean;
  setIsLeadEnabled: (val: boolean) => void;
  isTestimonialEnabled: boolean;
  setIsTestimonialEnabled: (val: boolean) => void;
  isThankYouNoteEnabled: boolean;
  setIsThankYouNoteEnabled: (val: boolean) => void;
  isNotesEnabled: boolean;
  setIsNotesEnabled: (val: boolean) => void;
  enableSpouse: boolean;
  setEnableSpouse: (val: boolean) => void;
  isMeetupEnabled: boolean;
  setIsMeetupEnabled: (val: boolean) => void;
  isProjectsEnabled: boolean;
  setIsProjectsEnabled: (val: boolean) => void;
  isProjectDashboardEnabled: boolean;
  setIsProjectDashboardEnabled: (val: boolean) => void;
  isPointsEnabled: boolean;
  setIsPointsEnabled: (val: boolean) => void;
  onboardingFormEnabled: boolean;
  setOnboardingFormEnabled: (val: boolean) => void;
  showUpcomingEventsInSocialDashboard: boolean;
  setShowUpcomingEventsInSocialDashboard: (val: boolean) => void;
  showCelebrations: boolean;
  setShowCelebrations: (val: boolean) => void;
  controlledBusinessListing: boolean;
  setControlledBusinessListing: (val: boolean) => void;
  businessLinkApprovalRequired: boolean;
  setBusinessLinkApprovalRequired: (val: boolean) => void;
  businessLinksMinPerMember: number | null;
  setBusinessLinksMinPerMember: (val: number | null) => void;
  businessLinksMaxPerMember: number | null;
  setBusinessLinksMaxPerMember: (val: number | null) => void;
  businessLinkPolicyVersion: number;
  isAdvertisementEnabled: boolean;
  setIsAdvertisementEnabled: (val: boolean) => void;
  businessDirectoryTitle: string;
  setBusinessDirectoryTitle: (val: string) => void;
  businessDirectoryPublic: boolean;
  setBusinessDirectoryPublic: (val: boolean) => void;
  businessDirectoryShowAddBusiness: boolean;
  setBusinessDirectoryShowAddBusiness: (val: boolean) => void;
}

export function FeatureSettings({
  communityId,
  mutateCommunity,
  isBusinessEnabled,
  setIsBusinessEnabled,
  isBusinessDirectoryEnabled,
  setIsBusinessDirectoryEnabled,
  isGivesAsksEnabled,
  setIsGivesAsksEnabled,
  isLeadEnabled,
  setIsLeadEnabled,
  isTestimonialEnabled,
  setIsTestimonialEnabled,
  isThankYouNoteEnabled,
  setIsThankYouNoteEnabled,
  isNotesEnabled,
  setIsNotesEnabled,
  enableSpouse,
  setEnableSpouse,
  isMeetupEnabled,
  setIsMeetupEnabled,
  isProjectsEnabled,
  setIsProjectsEnabled,
  isProjectDashboardEnabled,
  setIsProjectDashboardEnabled,
  isPointsEnabled,
  setIsPointsEnabled,
  onboardingFormEnabled,
  setOnboardingFormEnabled,
  showUpcomingEventsInSocialDashboard,
  setShowUpcomingEventsInSocialDashboard,
  showCelebrations,
  setShowCelebrations,
  controlledBusinessListing,
  setControlledBusinessListing,
  businessLinkApprovalRequired,
  setBusinessLinkApprovalRequired,
  businessLinksMinPerMember,
  setBusinessLinksMinPerMember,
  businessLinksMaxPerMember,
  setBusinessLinksMaxPerMember,
  businessLinkPolicyVersion,
  isAdvertisementEnabled,
  setIsAdvertisementEnabled,
  businessDirectoryTitle,
  setBusinessDirectoryTitle,
  businessDirectoryPublic,
  setBusinessDirectoryPublic,
  businessDirectoryShowAddBusiness,
  setBusinessDirectoryShowAddBusiness,
}: FeatureSettingsProps) {
  const [savingLimits, setSavingLimits] = useState(false);
  const [savingDirectoryTitle, setSavingDirectoryTitle] = useState(false);

  const updateFeature = async (key: string, value: boolean) => {
    try {
      await axiosInstance.patch(`/api/communities/${communityId}`, {
        [key]: value,
      });
      toast.success('Feature updated');
      await mutateCommunity();
    } catch (error) {
      console.error(error);
      toast.error('Failed to update feature');
    }
  };

  const saveDirectoryTitle = async () => {
    setSavingDirectoryTitle(true);
    try {
      const trimmed = businessDirectoryTitle.trim();
      await axiosInstance.patch(`/api/communities/${communityId}`, {
        businessDirectoryTitle: trimmed.length > 0 ? trimmed : null,
      });
      toast.success('Directory title saved');
      await mutateCommunity();
    } catch (error) {
      console.error(error);
      toast.error('Failed to save directory title');
    } finally {
      setSavingDirectoryTitle(false);
    }
  };

  const saveListingLimits = async () => {
    setSavingLimits(true);
    try {
      await axiosInstance.patch(`/api/communities/${communityId}`, {
        businessLinksMinPerMember: businessLinksMinPerMember,
        businessLinksMaxPerMember: businessLinksMaxPerMember,
      });
      toast.success('Listing limits saved');
      await mutateCommunity();
    } catch (error) {
      console.error(error);
      toast.error('Failed to save limits');
    } finally {
      setSavingLimits(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Community Features</CardTitle>
        <CardDescription>Enable or disable optional features for your community.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="rounded-xl border border-border bg-muted/20 dark:bg-muted/10 p-4 space-y-3">
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5">
              <Label className="text-base">Business dashboard</Label>
              <p className="text-sm text-muted-foreground">
                Show the Business tab on the community dashboard for leads, meetups, and related
                activity.
              </p>
            </div>
            <Switch
              checked={isBusinessEnabled}
              onCheckedChange={(checked) => {
                setIsBusinessEnabled(checked);
                void updateFeature('isBusinessEnabled', checked);
              }}
            />
          </div>

          {isBusinessEnabled ? (
            <div className="space-y-4 pt-3 border-t border-border/70 animate-in fade-in slide-in-from-top-2 duration-200">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Business toolkit
              </p>
              <div className="flex items-center justify-between gap-4">
                <div className="space-y-0.5">
                  <Label className="text-base">Lead generation</Label>
                  <p className="text-sm text-muted-foreground">
                    Enable tools for members to generate and share leads.
                  </p>
                </div>
                <Switch
                  checked={isLeadEnabled}
                  onCheckedChange={(checked) => {
                    setIsLeadEnabled(checked);
                    void updateFeature('isLeadEnabled', checked);
                  }}
                />
              </div>
              <div className="flex items-center justify-between gap-4">
                <div className="space-y-0.5">
                  <Label className="text-base">Testimonials</Label>
                  <p className="text-sm text-muted-foreground">
                    Allow members to give and receive testimonials.
                  </p>
                </div>
                <Switch
                  checked={isTestimonialEnabled}
                  onCheckedChange={(checked) => {
                    setIsTestimonialEnabled(checked);
                    void updateFeature('isTestimonialEnabled', checked);
                  }}
                />
              </div>
              <div className="flex items-center justify-between gap-4">
                <div className="space-y-0.5">
                  <Label className="text-base">Thank you notes</Label>
                  <p className="text-sm text-muted-foreground">
                    Enable sending thank-you notes for business and referrals.
                  </p>
                </div>
                <Switch
                  checked={isThankYouNoteEnabled}
                  onCheckedChange={(checked) => {
                    setIsThankYouNoteEnabled(checked);
                    void updateFeature('isThankYouNoteEnabled', checked);
                  }}
                />
              </div>
              <div className="flex items-center justify-between gap-4">
                <div className="space-y-0.5">
                  <Label className="text-base">Meetups</Label>
                  <p className="text-sm text-muted-foreground">
                    Allow members to announce and share meetings with others.
                  </p>
                </div>
                <Switch
                  checked={isMeetupEnabled}
                  onCheckedChange={(checked) => {
                    setIsMeetupEnabled(checked);
                    void updateFeature('isMeetupEnabled', checked);
                  }}
                />
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground pt-1 border-t border-border/70">
              Turn on the business dashboard to configure lead generation, testimonials, thank you
              notes, and meetups.
            </p>
          )}
        </div>

        <div className="flex items-center justify-between gap-4">
          <div className="space-y-0.5">
            <Label className="text-base">Points system</Label>
            <p className="text-sm text-muted-foreground">
              Enable points tracking and trigger rewards for members.
            </p>
          </div>
          <Switch
            checked={isPointsEnabled}
            onCheckedChange={(checked) => {
              setIsPointsEnabled(checked);
              void updateFeature('isPointsEnabled', checked);
            }}
          />
        </div>

        <div className="rounded-xl border border-border bg-muted/20 dark:bg-muted/10 p-4 space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5">
              <Label className="text-base">Gives & Asks</Label>
              <p className="text-sm text-muted-foreground">
                Let members share gives and asks, reply to asks with leads, and use them in conclave events.
              </p>
            </div>
            <Switch
              checked={isGivesAsksEnabled}
              onCheckedChange={(checked) => {
                setIsGivesAsksEnabled(checked);
                void updateFeature('isGivesAsksEnabled', checked);
              }}
            />
          </div>
        </div>

        <div className="rounded-xl border border-border bg-muted/20 dark:bg-muted/10 p-4 space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5">
              <Label className="text-base">Business directory</Label>
              <p className="text-sm text-muted-foreground">
                Enable the searchable business directory page and Directory navigation link.
              </p>
            </div>
            <Switch
              checked={isBusinessDirectoryEnabled}
              onCheckedChange={(checked) => {
                setIsBusinessDirectoryEnabled(checked);
                void updateFeature('isBusinessDirectoryEnabled', checked);
              }}
            />
          </div>

          {isBusinessDirectoryEnabled ? (
            <div className="space-y-4 pt-2 border-t border-border/70 animate-in fade-in slide-in-from-top-2 duration-200">
              <Card className="border-primary/20 bg-gradient-to-br from-card to-primary/5">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Directory page</CardTitle>
                  <CardDescription>
                    Customize the public business directory URL, heading, and visitor prompts.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="directory-title">Page heading</Label>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Input
                        id="directory-title"
                        placeholder="Business Directory"
                        value={businessDirectoryTitle}
                        onChange={(e) => setBusinessDirectoryTitle(e.target.value)}
                        maxLength={100}
                      />
                      <Button
                        type="button"
                        variant="secondary"
                        className="shrink-0"
                        disabled={savingDirectoryTitle}
                        onClick={() => void saveDirectoryTitle()}
                      >
                        {savingDirectoryTitle ? 'Saving…' : 'Save title'}
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Leave empty to use the default heading &quot;Business Directory&quot;. Share
                      link uses your community slug: /your-slug/business
                    </p>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <div className="space-y-0.5">
                      <Label className="text-base">Public directory link</Label>
                      <p className="text-sm text-muted-foreground">
                        Anyone with the community slug link can view the directory without signing
                        in.
                      </p>
                    </div>
                    <Switch
                      checked={businessDirectoryPublic}
                      onCheckedChange={(checked) => {
                        setBusinessDirectoryPublic(checked);
                        void updateFeature('businessDirectoryPublic', checked);
                      }}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <div className="space-y-0.5">
                      <Label className="text-base">Show &quot;Add your business&quot;</Label>
                      <p className="text-sm text-muted-foreground">
                        Prompt visitors, non-members, and members without a listed business to add
                        one.
                      </p>
                    </div>
                    <Switch
                      checked={businessDirectoryShowAddBusiness}
                      onCheckedChange={(checked) => {
                        setBusinessDirectoryShowAddBusiness(checked);
                        void updateFeature('businessDirectoryShowAddBusiness', checked);
                      }}
                    />
                  </div>
                </CardContent>
              </Card>

              <div className="rounded-lg border border-border/70 bg-background/50 p-4 space-y-4">
                <div className="space-y-1">
                  <Label className="text-base">Curated directory (map-based listings)</Label>
                  <p className="text-sm text-muted-foreground">
                    When on, the business directory only shows businesses members have explicitly
                    linked. When off, any joined member&apos;s businesses appear automatically
                    (legacy behavior).
                  </p>
                  {businessLinkPolicyVersion > 0 && (
                    <p className="text-xs text-muted-foreground">
                      Policy version {businessLinkPolicyVersion}
                    </p>
                  )}
                </div>
                <div className="flex items-center justify-between gap-4">
                  <span className="text-sm font-medium">Use curated listings</span>
                  <Switch
                    checked={controlledBusinessListing}
                    onCheckedChange={(checked) => {
                      setControlledBusinessListing(checked);
                      void updateFeature('controlledBusinessListing', checked);
                    }}
                  />
                </div>

                {controlledBusinessListing ? (
                  <div className="space-y-4 pt-2 border-t border-border/70">
                    <div className="flex items-center justify-between gap-4">
                      <div className="space-y-0.5">
                        <Label className="text-base">Require admin approval</Label>
                        <p className="text-sm text-muted-foreground">
                          New member listings wait for an admin to approve before appearing in the
                          directory.
                        </p>
                      </div>
                      <Switch
                        checked={businessLinkApprovalRequired}
                        onCheckedChange={(checked) => {
                          setBusinessLinkApprovalRequired(checked);
                          void updateFeature('businessLinkApprovalRequired', checked);
                        }}
                      />
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="biz-min">Min listings per member</Label>
                        <Input
                          id="biz-min"
                          type="number"
                          min={0}
                          inputMode="numeric"
                          placeholder="No minimum"
                          value={businessLinksMinPerMember ?? ''}
                          onChange={(e) => {
                            const raw = e.target.value;
                            if (raw === '') setBusinessLinksMinPerMember(null);
                            else {
                              const n = Number.parseInt(raw, 10);
                              if (!Number.isNaN(n)) setBusinessLinksMinPerMember(n);
                            }
                          }}
                        />
                        <p className="text-xs text-muted-foreground">Leave empty for no minimum.</p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="biz-max">Max listings per member</Label>
                        <Input
                          id="biz-max"
                          type="number"
                          min={0}
                          inputMode="numeric"
                          placeholder="No maximum"
                          value={businessLinksMaxPerMember ?? ''}
                          onChange={(e) => {
                            const raw = e.target.value;
                            if (raw === '') setBusinessLinksMaxPerMember(null);
                            else {
                              const n = Number.parseInt(raw, 10);
                              if (!Number.isNaN(n)) setBusinessLinksMaxPerMember(n);
                            }
                          }}
                        />
                        <p className="text-xs text-muted-foreground">Leave empty for no cap.</p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => void saveListingLimits()}
                      disabled={savingLimits}
                    >
                      {savingLimits ? 'Saving…' : 'Save limits'}
                    </Button>
                  </div>
                ) : null}
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground pt-1 border-t border-border/70">
              Turn on the business directory to configure the directory page and curated listings.
            </p>
          )}
        </div>

        <div className="flex items-center justify-between gap-4">
          <div className="space-y-0.5">
            <Label className="text-base">Member notes</Label>
            <p className="text-sm text-muted-foreground">
              Allow members to write private notes about other members.
            </p>
          </div>
          <Switch
            checked={isNotesEnabled}
            onCheckedChange={(checked) => {
              setIsNotesEnabled(checked);
              void updateFeature('isNotesEnabled', checked);
            }}
          />
        </div>

        <div className="flex items-center justify-between gap-4">
          <div className="space-y-0.5">
            <Label className="text-base">Enable Spouse Grouping</Label>
            <p className="text-sm text-muted-foreground">
              Show spouse members grouped together in a highlighted box.
            </p>
          </div>
          <Switch
            checked={enableSpouse}
            onCheckedChange={(checked) => {
              setEnableSpouse(checked);
              void updateFeature('enableSpouse', checked);
            }}
          />
        </div>

        <div className="flex items-center justify-between gap-4">
          <div className="space-y-0.5">
            <Label className="text-base">Projects</Label>
            <p className="text-sm text-muted-foreground">
              Allow members to create and manage community projects.
            </p>
          </div>
          <Switch
            checked={isProjectsEnabled}
            onCheckedChange={(checked) => {
              setIsProjectsEnabled(checked);
              void updateFeature('isProjectsEnabled', checked);
            }}
          />
        </div>

        <div className="flex items-center justify-between gap-4">
          <div className="space-y-0.5">
            <Label className="text-base">Project dashboard</Label>
            <p className="text-sm text-muted-foreground">
              Show projects tab for vendors to view and submit proposals.
            </p>
          </div>
          <Switch
            checked={isProjectDashboardEnabled}
            onCheckedChange={(checked) => {
              setIsProjectDashboardEnabled(checked);
              void updateFeature('isProjectDashboardEnabled', checked);
            }}
          />
        </div>

        <div className="flex items-center justify-between gap-4">
          <div className="space-y-0.5">
            <Label className="text-base">Additional details</Label>
            <p className="text-sm text-muted-foreground">
              Ask new members for extra information when they join.
            </p>
          </div>
          <Switch
            checked={onboardingFormEnabled}
            onCheckedChange={(checked) => {
              setOnboardingFormEnabled(checked);
              void updateFeature('onboardingFormEnabled', checked);
            }}
          />
        </div>

        <div className="flex items-center justify-between gap-4">
          <div className="space-y-0.5">
            <Label className="text-base">Upcoming events in dashboard</Label>
            <p className="text-sm text-muted-foreground">
              Show upcoming community events in the social and business dashboards.
            </p>
          </div>
          <Switch
            checked={showUpcomingEventsInSocialDashboard}
            onCheckedChange={(checked) => {
              setShowUpcomingEventsInSocialDashboard(checked);
              void updateFeature('showUpcomingEventsInSocialDashboard', checked);
            }}
          />
        </div>

        <div className="flex items-center justify-between gap-4">
          <div className="space-y-0.5">
            <Label className="text-base">Community celebrations</Label>
            <p className="text-sm text-muted-foreground">
              Display birthdays and other celebrations in the community.
            </p>
          </div>
          <Switch
            checked={showCelebrations}
            onCheckedChange={(checked) => {
              setShowCelebrations(checked);
              void updateFeature('showCelebrations', checked);
            }}
          />
        </div>

        <div className="flex items-center justify-between gap-4">
          <div className="space-y-0.5">
            <Label className="text-base">Advertisements</Label>
            <p className="text-sm text-muted-foreground">Enable advertisements in dashboard.</p>
          </div>
          <Switch
            checked={isAdvertisementEnabled}
            onCheckedChange={(checked) => {
              setIsAdvertisementEnabled(checked);
              void updateFeature('isAdvertisementEnabled', checked);
            }}
          />
        </div>
      </CardContent>
    </Card>
  );
}
